import React, { useEffect, useState, useContext } from "react";
import { api, AssetLocation, AssetLocationTypes, ExtendedLocationProperties, IAssetLocationsByParent, IContext, IRequestState, KeyManagementTypes, RequestStatus } from "../../../logic/api";
import TreeViewNode from "../TreeViewNode";
import propTypes from "prop-types";
import { PagedDataView } from "../../lists/PagedDataView/PagedDataView";
import { ITreeViewLoaderProps, ITreeViewRecord } from "../TreeView";
import { useTranslation } from "react-i18next";
import { ReleaseFlag, useReleaseManagement } from "../../../utility/feature-management";
import { isBoolean } from "lodash";
import { TreeViewContext } from "../TreeView";

interface AssetLocationTreeNodeProps {
    location: AssetLocation;
    expanded: boolean;
}

interface IFlags {
    types: AssetLocationTypes[];
    selectionTypes: AssetLocationTypes[];
    keyManagementTypes: KeyManagementTypes[];
    displayInactiveLocations?: boolean;
    filter: string;
    code: string | null;
    arrivalsOnly?: boolean;
    exactMatch?: boolean;
}

const LocationsTreeViewLoader: React.FunctionComponent<ITreeViewLoaderProps & {
    onDragReorder?: (draggedRecord: ITreeViewRecord, targetRecord: ITreeViewRecord, position: 'above' | 'below') => void,
}> = (props) => {
    const isFilteredLocationSearchReleased = useReleaseManagement(ReleaseFlag.releaseLocationHierarchySearchFilter);

    const flags: IFlags = (props.flags || {}) as IFlags;

    const tmpParams = {
        parentId: props.parentId,
        types: flags == null || flags.types == null ? [] : flags.types,
        includeInActive: isBoolean(flags.displayInactiveLocations) ? flags.displayInactiveLocations : false,
        includeInVisible: false,
        filter: flags == null ? "" : (flags.filter || ""),
        selectionTypes: flags == null || flags.selectionTypes == null ? [] : flags.selectionTypes,
        keyManagementTypes: flags == null || flags.keyManagementTypes == null ? [] : flags.keyManagementTypes,
        arrivalsOnly: flags.arrivalsOnly == true,
        includeExtendedProperties: [ExtendedLocationProperties.all],
        ...(isFilteredLocationSearchReleased ? { exactMatch: flags.exactMatch === true } : {})
    };

    const { t } = useTranslation();
    const [params, setParams] = useState<IAssetLocationsByParent>(tmpParams);
    const [state, setState] = useState<IRequestState<AssetLocationTreeNodeProps[], IAssetLocationsByParent>>({
        data: null,
        status: RequestStatus.idle,
        err: null,
        params: null,
        callBack: () => undefined,
        context: null
    });

    const isUnknownMovementReleased = useReleaseManagement(ReleaseFlag.releaseUnknownMovement);

    const tvc = useContext(TreeViewContext);
    const enableDragAndDrop = tvc.enableDragAndDrop;

    useEffect(() => {
        setParams(tmpParams);
    }, [flags]);

    const apiHookHandler = () => {
        const mapLocation = (location: AssetLocation): AssetLocationTreeNodeProps => {
            const expanded = isUnknownMovementReleased && props.defaultExpanded ? props.defaultExpanded.includes(location.type) : location.type === AssetLocationTypes.CarPark;
            return { location, expanded }
        };

        const mapLocations = (locations: AssetLocation[] | null) =>
            locations
                ? locations.map(mapLocation)
                : null;

        const apiHook = api.hooks.assetLocation.getByParent();

        return {
            ...state,
            send: (context: IContext, parameters: IAssetLocationsByParent, callback?: (value: AssetLocationTreeNodeProps[] | null) => void) => {
                setState({ ...state, status: RequestStatus.loading, data: null });

                const myCallback = (assetLocations: AssetLocation[] | null) => {
                    const mappedLocations = mapLocations(assetLocations);
                    setState({ ...state, status: RequestStatus.loaded, data: mappedLocations });

                    if (callback) {
                        callback(mappedLocations);
                    }
                };

                apiHook.send(context, parameters, myCallback);
            }
        }
    };

    return <PagedDataView<IAssetLocationsByParent, AssetLocationTreeNodeProps>
        apiHook={apiHookHandler}
        parameters={params}
        animate={props.parentId != 0}
        animateDelay={0.02}
        renderItem={(item) => (
            <TreeViewNode
                record={item.location}
                expanded={item.expanded}
                {...(enableDragAndDrop ? {
                    onDragReorder: props.onDragReorder
                } : {})}
            />
        )}>
        {tmpParams.filter.length > 0 && (
            <div>{t("treeView.noLocations")}</div>
        )}
    </PagedDataView>
}

LocationsTreeViewLoader.propTypes = {
    parentId: propTypes.number.isRequired,
    flags: propTypes.any
}

export default LocationsTreeViewLoader;
